import { useMemo, useCallback, useEffect } from 'react';
import { useGetTableConfigurationByName, useSaveTableConfigurationMutation } from '@frontend/app/hooks';
import type { SocialAccountData } from '../components/social-accounts-table/social-accounts-table-types';
import type { SocialAccountsColumn } from '../components/social-accounts-table/social-accounts-column-dialog';

// Table configuration constants
const SOCIAL_ACCOUNTS_TABLE_COLUMN_CONFIG_VERSION = 1;
const SOCIAL_ACCOUNTS_TABLE_NAME = 'social-ads-social-accounts-table';

export interface UseSocialAdsSocialAccountsTab {
  isLoading: boolean;
  isRefreshing: boolean;
  data: SocialAccountData[];
  selectedColumns: SocialAccountsColumn[];
  setSelectedColumns: (
    columns: SocialAccountsColumn[] | ((prev: SocialAccountsColumn[]) => SocialAccountsColumn[]),
  ) => void;
  onSaveColumns?: (columns: SocialAccountsColumn[]) => Promise<void>;
}

export function useSocialAdsSocialAccountsTab(params: {
  isLoading: boolean;
  isRefreshing: boolean;
  data: SocialAccountData[];
  selectedColumns: SocialAccountsColumn[];
  setSelectedColumns: (
    columns: SocialAccountsColumn[] | ((prev: SocialAccountsColumn[]) => SocialAccountsColumn[]),
  ) => void;
  onSaveColumns?: (columns: SocialAccountsColumn[]) => Promise<void>;
}): UseSocialAdsSocialAccountsTab {
  // Table configuration hooks
  const [saveTableConfiguration] = useSaveTableConfigurationMutation();
  const { data: tableConfigData, loading: tableConfigLoading } = useGetTableConfigurationByName({
    variables: { tableName: SOCIAL_ACCOUNTS_TABLE_NAME },
  });

  // Default columns for social accounts table
  const defaultSocialAccountsColumns: SocialAccountsColumn[] = useMemo(
    () => [
      { id: 'socialAccount', header: 'Social Account', accessorKey: 'socialAccount', hidden: false, order: 1 },
      { id: 'memberName', header: 'Member Name', accessorKey: 'memberName', hidden: false, order: 2 },
      { id: 'roas', header: 'ROAS', accessorKey: 'roas', hidden: false, order: 3 },
      { id: 'impressions', header: 'Impressions', accessorKey: 'impressions', hidden: false, order: 4 },
      { id: 'reach', header: 'Reach', accessorKey: 'reach', hidden: false, order: 5 },
      { id: 'clicks', header: 'Clicks', accessorKey: 'clicks', hidden: false, order: 6 },
      { id: 'ctr', header: 'CTR', accessorKey: 'ctr', hidden: false, order: 7 },
      { id: 'cpm', header: 'CPM', accessorKey: 'cpm', hidden: false, order: 8 },
      { id: 'cpc', header: 'CPC', accessorKey: 'cpc', hidden: false, order: 9 },
      { id: 'spend', header: 'Spend', accessorKey: 'spend', hidden: false, order: 10 },
      { id: 'sales', header: 'Sales', accessorKey: 'sales', hidden: false, order: 11 },
    ],
    [],
  );

  // Merge saved configuration with defaults
  const mergedColumns = useMemo(() => {
    if (tableConfigLoading) {
      return defaultSocialAccountsColumns;
    }

    if (
      tableConfigData?.getTableConfigurationByName?.columnDefinitions &&
      tableConfigData?.getTableConfigurationByName?.version === SOCIAL_ACCOUNTS_TABLE_COLUMN_CONFIG_VERSION
    ) {
      // Get saved configuration
      const savedColumns = tableConfigData.getTableConfigurationByName.columnDefinitions as SocialAccountsColumn[];

      // Create a map of saved columns for quick lookup
      const savedColumnsMap = new Map(savedColumns.map((col) => [col.id, col]));

      // Merge saved configuration with defaults, preserving order
      const mergedColumns = defaultSocialAccountsColumns.map((defaultCol) => {
        const savedCol = savedColumnsMap.get(defaultCol.id);
        if (savedCol) {
          return {
            ...defaultCol,
            hidden: savedCol.hidden,
            order: savedCol.order,
          };
        }
        return defaultCol;
      });

      // Sort by the saved order
      return mergedColumns.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    return defaultSocialAccountsColumns;
  }, [tableConfigLoading, tableConfigData, defaultSocialAccountsColumns]);

  // Apply merged columns when available
  useEffect(() => {
    if (mergedColumns && mergedColumns.length > 0 && !tableConfigLoading) {
      params.setSelectedColumns(mergedColumns);
    }
  }, [mergedColumns, tableConfigLoading, params.setSelectedColumns]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle saving column preferences for Social Accounts table
  const handleSaveSocialAccountsColumns = useCallback(
    async (updatedColumns: SocialAccountsColumn[]) => {
      try {
        // Save to backend
        await saveTableConfiguration({
          variables: {
            data: {
              version: SOCIAL_ACCOUNTS_TABLE_COLUMN_CONFIG_VERSION,
              tableName: SOCIAL_ACCOUNTS_TABLE_NAME,
              columnDefinitions: updatedColumns,
            },
          },
        });

        // Update local state
        params.setSelectedColumns(updatedColumns);
        console.log('Social accounts column preferences saved successfully');
      } catch (error) {
        console.error('Failed to save social accounts column preferences:', error);
        throw error;
      }
    },
    [params, saveTableConfiguration],
  );

  return useMemo(
    () => ({
      isLoading: params.isLoading,
      isRefreshing: params.isRefreshing,
      data: params.data,
      selectedColumns: params.selectedColumns,
      setSelectedColumns: params.setSelectedColumns,
      onSaveColumns: handleSaveSocialAccountsColumns,
    }),
    [
      params.isLoading,
      params.isRefreshing,
      params.data,
      params.selectedColumns,
      params.setSelectedColumns,
      handleSaveSocialAccountsColumns,
    ],
  );
}
